import { useState, useMemo } from 'react'
import { Search, Clock, ArrowRight, Tag } from 'lucide-react'
import { blogPosts, getBlogTags } from '../lib/blog-data'
import type { BlogPost } from '../lib/types'

interface BlogListProps {
  onSelectPost: (post: BlogPost) => void
}

export function BlogList({ onSelectPost }: BlogListProps) {
  const [search, setSearch] = useState('')
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const allTags = useMemo(() => getBlogTags(), [])

  const filtered = useMemo(() => {
    let posts = blogPosts
    if (activeTag) {
      posts = posts.filter(p => p.tags.includes(activeTag))
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      posts = posts.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      )
    }
    return posts
  }, [search, activeTag])

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-8 lg:px-8">
        <div className="mb-8">
          <h1 className="text-h2 text-[hsl(var(--fg))] font-bold mb-2">Scholars' Corner</h1>
          <p className="text-body text-[hsl(var(--fg-muted))] max-w-2xl">
            In-depth articles on Islamic theology, comparative religion, scripture analysis, and responses to common questions.
          </p>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--fg-muted))]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search articles..."
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] pl-10 pr-4 py-2.5 text-small text-[hsl(var(--fg))] outline-none placeholder:text-[hsl(var(--fg-dim))] focus:border-[hsl(var(--accent)/0.5)] transition-colors"
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveTag(null)}
            className={`rounded-full px-3 py-1 text-caption font-medium transition-all ${
              !activeTag
                ? 'bg-[hsl(var(--accent))] text-white'
                : 'bg-[hsl(var(--elevated))] text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))] border border-[hsl(var(--border))]'
            }`}
          >
            All
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag === activeTag ? null : tag)}
              className={`rounded-full px-3 py-1 text-caption font-medium transition-all ${
                activeTag === tag
                  ? 'bg-[hsl(var(--accent))] text-white'
                  : 'bg-[hsl(var(--elevated))] text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))] border border-[hsl(var(--border))]'
              }`}
            >
              <Tag className="h-3 w-3 inline mr-1 -mt-0.5" />
              {tag}
            </button>
          ))}
        </div>

        <div className="space-y-6">
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-body text-[hsl(var(--fg-muted))]">No articles found</p>
              <p className="text-small text-[hsl(var(--fg-dim))] mt-1">Try a different search or tag filter</p>
            </div>
          )}
          {filtered.map(post => (
            <article
              key={post.id}
              onClick={() => onSelectPost(post)}
              className="group cursor-pointer rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 transition-all hover:border-[hsl(var(--accent)/0.3)] hover:shadow-md"
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3 text-caption text-[hsl(var(--fg-muted))]">
                  <span>{post.date}</span>
                  <span className="w-1 h-1 rounded-full bg-[hsl(var(--fg-dim))]" />
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {post.readingTime} min read
                  </span>
                </div>
                <h2 className="text-h4 text-[hsl(var(--fg))] font-semibold group-hover:text-[hsl(var(--accent))] transition-colors">
                  {post.title}
                </h2>
                <p className="text-body text-[hsl(var(--fg-muted))] leading-relaxed">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-1.5">
                    {post.tags.map(tag => (
                      <span
                        key={tag}
                        className="rounded-md bg-[hsl(var(--accent-soft))] px-2 py-0.5 text-caption text-[hsl(var(--accent))]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <ArrowRight className="h-4 w-4 text-[hsl(var(--fg-muted))] group-hover:text-[hsl(var(--accent))] transition-colors shrink-0" />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  )
}
